import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Upload, Download, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { api } from '@/lib/api.js';

const REQUIRED_FIELDS = ['emp_no', 'component_code', 'effective_from', 'amount'];
const OPTIONAL_FIELDS = ['effective_to'];
const ALL_FIELDS = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];

const BASE_FIELD_LABELS = {
  emp_no: 'Employee No',
  effective_from: 'Effective From',
  amount: 'Amount',
  effective_to: 'Effective To (optional)',
};

function getFieldLabels(type) {
  return { ...BASE_FIELD_LABELS, component_code: type === 'deduction' ? 'Deduction Code' : 'Allowance Code' };
}

function isValidDate(v) {
  if (!v) return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(String(v)) && !isNaN(Date.parse(String(v)));
}

function isPositiveNumber(v) {
  const n = Number(v);
  return !isNaN(n) && n > 0;
}

function validateRow(row) {
  const errs = [];
  if (!row.emp_no) errs.push('emp_no is required');
  if (!row.component_code) errs.push('component_code is required');
  if (!row.effective_from) errs.push('effective_from is required');
  else if (!isValidDate(row.effective_from)) errs.push('effective_from must be a valid date (YYYY-MM-DD)');
  if (!row.amount && row.amount !== 0) errs.push('amount is required');
  else if (!isPositiveNumber(row.amount)) errs.push('amount must be a positive number');
  if (row.effective_to) {
    if (!isValidDate(row.effective_to)) errs.push('effective_to must be a valid date (YYYY-MM-DD)');
    else if (new Date(row.effective_to) < new Date(row.effective_from)) errs.push('effective_to must be ≥ effective_from');
  }
  return errs;
}

function downloadTemplate(type) {
  const codeHeader = type === 'deduction' ? 'deduction_code' : 'allowance_code';
  const sampleCode = type === 'deduction' ? 'EPF' : 'TRVL';
  const headers = ['emp_no', codeHeader, 'effective_from', 'amount', 'effective_to'];
  const sample = [['E001', sampleCode, '2026-01-01', '5000', ''], ['E002', type === 'deduction' ? 'ETF' : 'MEAL', '2026-01-01', '2500', '2026-12-31']];
  const ws = XLSX.utils.aoa_to_sheet([headers, ...sample]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, type === 'deduction' ? 'Deductions' : 'Allowances');
  XLSX.writeFile(wb, `${type}_import_template.csv`);
}

// Step indicators
const STEPS = ['Upload', 'Map Columns', 'Preview & Import'];

export function AllowanceImportDialog({ open, onOpenChange, onSuccess, type = 'allowance' }) {
  const FIELD_LABELS = getFieldLabels(type);
  const dialogTitle = type === 'deduction' ? 'Import Deductions' : 'Import Allowances';
  const [step, setStep] = useState(0);
  const [rawRows, setRawRows] = useState([]);      // array of objects from SheetJS
  const [headers, setHeaders] = useState([]);       // column headers from file
  const [mapping, setMapping] = useState({});       // fieldName → headerName
  const [preview, setPreview] = useState([]);       // mapped + validated rows
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);       // { imported, errors }
  const fileRef = useRef(null);

  function reset() {
    setStep(0);
    setRawRows([]);
    setHeaders([]);
    setMapping({});
    setPreview([]);
    setResult(null);
    setSubmitting(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleClose(v) {
    if (!v) reset();
    onOpenChange(v);
  }

  // --- Step A: parse file ---
  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const data = new Uint8Array(ev.target.result);
      const wb = XLSX.read(data, { type: 'array', raw: false, dateNF: 'YYYY-MM-DD' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      if (rows.length === 0) return;
      const hdrs = Object.keys(rows[0]);
      setHeaders(hdrs);
      setRawRows(rows);
      // Auto-map: exact case-insensitive match; also match legacy column names
      const codeAliases = type === 'deduction' ? ['deduction_code', 'component_code'] : ['allowance_code', 'component_code'];
      const autoMap = {};
      for (const field of ALL_FIELDS) {
        const candidates = field === 'component_code' ? codeAliases : [field];
        const match = hdrs.find((h) => candidates.includes(h.trim().toLowerCase()));
        if (match) autoMap[field] = match;
      }
      setMapping(autoMap);
      setStep(1);
    };
    reader.readAsArrayBuffer(file);
  }

  // --- Step B: build preview from mapping ---
  function buildPreview() {
    const mapped = rawRows.map((raw, idx) => {
      const row = { _idx: idx + 1 };
      for (const field of ALL_FIELDS) {
        const hdr = mapping[field];
        row[field] = hdr ? String(raw[hdr] ?? '').trim() : '';
      }
      row._errors = validateRow(row);
      return row;
    });
    setPreview(mapped);
    setStep(2);
  }

  // --- Step C: submit ---
  async function handleSubmit() {
    const validRows = preview.filter((r) => r._errors.length === 0);
    if (validRows.length === 0) return;
    setSubmitting(true);
    try {
      const payload = validRows.map(({ emp_no, component_code, effective_from, amount, effective_to }) => ({
        emp_no,
        component_code,
        effective_from,
        amount: Number(amount),
        ...(effective_to ? { effective_to } : {}),
      }));
      const res = await api.importSalaryComponents(type, payload);
      setResult(res);
      onSuccess?.(res);
    } catch (err) {
      setResult({ imported: 0, errors: [{ row: '-', emp_no: '-', component_code: '-', message: err.message }] });
    } finally {
      setSubmitting(false);
    }
  }

  const hasErrors = preview.some((r) => r._errors.length > 0);
  const validCount = preview.filter((r) => r._errors.length === 0).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-4">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                i < step ? 'bg-brand-500 text-white' : i === step ? 'bg-brand-100 text-brand-700 ring-2 ring-brand-500 dark:bg-brand-900 dark:text-brand-300' : 'bg-slate-100 text-slate-400 dark:bg-slate-800'
              }`}>{i + 1}</div>
              <span className={`text-sm ${i === step ? 'font-medium text-slate-900 dark:text-slate-100' : 'text-slate-400'}`}>{label}</span>
              {i < STEPS.length - 1 && <span className="text-slate-200 dark:text-slate-700 mx-1">›</span>}
            </div>
          ))}
        </div>

        {/* Step A — Upload */}
        {step === 0 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Upload a <strong>.csv</strong>, <strong>.xlsx</strong>, or <strong>.xls</strong> file. The first row must be a header row.
            </p>
            <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-8 cursor-pointer hover:border-brand-400 transition-colors">
              <Upload className="h-8 w-8 text-slate-400" />
              <span className="text-sm text-slate-500">Click to browse or drag & drop</span>
              <span className="text-xs text-slate-400">.csv, .xlsx, .xls</span>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="sr-only"
                onChange={handleFile}
              />
            </label>
            <div className="flex justify-between items-center pt-1">
              <span className="text-xs text-slate-400">Need a template?</span>
              <Button variant="outline" size="sm" type="button" onClick={() => downloadTemplate(type)}>
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Download Template
              </Button>
            </div>
          </div>
        )}

        {/* Step B — Map Columns */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Match your file's columns to the required fields. <span className="font-medium">{rawRows.length} rows</span> detected.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {ALL_FIELDS.map((field) => (
                <div key={field} className="space-y-1">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    {FIELD_LABELS[field]}
                    {REQUIRED_FIELDS.includes(field) && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <Select
                    value={mapping[field] || ''}
                    onChange={(e) => setMapping({ ...mapping, [field]: e.target.value || undefined })}
                  >
                    <option value="">{OPTIONAL_FIELDS.includes(field) ? '— Not mapped —' : '— Select column —'}</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </Select>
                </div>
              ))}
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
              <Button
                onClick={buildPreview}
                disabled={REQUIRED_FIELDS.some((f) => !mapping[f])}
              >
                Preview →
              </Button>
            </div>
          </div>
        )}

        {/* Step C — Preview */}
        {step === 2 && !result && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                <span className="font-medium text-green-600 dark:text-green-400">{validCount} valid</span>
                {hasErrors && (
                  <span className="ml-2 font-medium text-red-500">{preview.length - validCount} with errors</span>
                )}
                {' '}rows to import
              </p>
              {hasErrors && (
                <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Error rows will be skipped
                </div>
              )}
            </div>

            <div className="overflow-auto max-h-72 rounded border border-slate-200 dark:border-slate-700">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-slate-500">#</th>
                    {REQUIRED_FIELDS.map((f) => (
                      <th key={f} className="px-2 py-1.5 text-left text-slate-500 whitespace-nowrap">{FIELD_LABELS[f]}</th>
                    ))}
                    <th className="px-2 py-1.5 text-left text-slate-500 whitespace-nowrap">{FIELD_LABELS.effective_to}</th>
                    <th className="px-2 py-1.5 text-left text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {preview.map((row) => {
                    const hasErr = row._errors.length > 0;
                    return (
                      <tr key={row._idx} className={hasErr ? 'bg-red-50 dark:bg-red-950/30' : ''}>
                        <td className="px-2 py-1.5 text-slate-400">{row._idx}</td>
                        {REQUIRED_FIELDS.map((f) => {
                          const fieldErr = row._errors.some((e) => e.toLowerCase().includes(f.replace(/_/g, ' ')));
                          return (
                            <td key={f} className={`px-2 py-1.5 whitespace-nowrap font-mono ${fieldErr ? 'text-red-600 dark:text-red-400' : ''}`}>
                              {row[f] || <span className="text-slate-300">—</span>}
                            </td>
                          );
                        })}
                        <td className="px-2 py-1.5 whitespace-nowrap font-mono text-slate-500">
                          {row.effective_to || '—'}
                        </td>
                        <td className="px-2 py-1.5">
                          {hasErr ? (
                            <span className="text-red-500 flex items-start gap-1">
                              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              <span>{row._errors[0]}{row._errors.length > 1 ? ` (+${row._errors.length - 1})` : ''}</span>
                            </span>
                          ) : (
                            <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              OK
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between pt-1">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={handleSubmit} disabled={submitting || validCount === 0}>
                {submitting ? 'Importing…' : `Import ${validCount} row${validCount !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        )}

        {/* Result */}
        {step === 2 && result && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg bg-green-50 dark:bg-green-950/30 px-4 py-3 text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-5 w-5 shrink-0" />
              <span className="font-medium">{result.imported} row{result.imported !== 1 ? 's' : ''} imported successfully</span>
            </div>

            {result.errors?.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-red-600 dark:text-red-400 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {result.errors.length} row{result.errors.length !== 1 ? 's' : ''} failed
                </p>
                <div className="rounded border border-red-200 dark:border-red-800 overflow-auto max-h-40">
                  <table className="min-w-full text-xs">
                    <thead className="bg-red-50 dark:bg-red-950/40">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-red-500">Row</th>
                        <th className="px-2 py-1.5 text-left text-red-500">emp_no</th>
                        <th className="px-2 py-1.5 text-left text-red-500">component_code</th>
                        <th className="px-2 py-1.5 text-left text-red-500">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-100 dark:divide-red-900">
                      {result.errors.map((e, i) => (
                        <tr key={i}>
                          <td className="px-2 py-1.5">{e.row}</td>
                          <td className="px-2 py-1.5 font-mono">{e.emp_no}</td>
                        <td className="px-2 py-1.5 font-mono">{e.component_code}</td>
                          <td className="px-2 py-1.5 text-red-600 dark:text-red-400">{e.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => { reset(); onOpenChange(false); }}>Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
